import React, { createContext, CSSProperties, useContext, useEffect, useMemo, useState } from "react";
import { LucideIcon } from "../../icon/LucideIcon";
import styles from "./Tile.module.css";
import { FullyCustomResizeHandle } from "../misc/CustomResizeHandle";

interface MainProps {
  style?: CSSProperties;
  children: React.ReactNode;
}
interface TileContextStore {
  resizible?: boolean;
  foldable: boolean;
  isFolded: boolean;
  setIsFolded: React.Dispatch<React.SetStateAction<boolean>>;
}
const TileContext = createContext<TileContextStore | null>(null);

interface TileProps extends MainProps {
  foldable?: boolean;
  isFolded?: boolean;
  resizable?: boolean;
  disabled?: boolean;
}
/**
 * A functional component that represents a Tile, which can be foldable, resizable, and disabled.
 * It provides a context to its children with the current state and functionality.
 * @author @CorradoSurmanowicz
 * @param {TileProps} props - The properties passed to the Tile component.
 * @param {boolean} [props.foldable=true] - Determines if the tile can be folded.
 * @param {boolean} [props.isFolded=false] - Indicates the initial folded state of the tile.
 * @param {boolean} [props.resizible=false] - Determines if the tile can be resized.
 * @param {boolean} [props.disabled=false] - Indicates if the tile is disabled.
 * @param {React.CSSProperties} [props.style] - Additional styles to apply to the tile.
 * @param {React.ReactNode} props.children - The content to be rendered inside the tile.
 *
 * @returns {JSX.Element} The rendered Tile component.
 */
export const Tile = ({
  foldable = true,
  isFolded = false,
  resizable = false,
  disabled = false,
  style,
  children,
}: TileProps) => {
  const [_isFolded, setIsFolded] = useState(isFolded);

  useEffect(() => {
    setIsFolded(isFolded);
  }, [isFolded]);

  const context = useMemo(
    () => ({ foldable, resizible: resizable, isFolded: _isFolded, setIsFolded }),
    [_isFolded, foldable, resizable]
  );
  return (
    <TileContext.Provider value={context}>
      <div
        className={`${styles.tile} ${disabled && styles.tile_disabled}`}
        style={{ ...style, ...(_isFolded && { height: "fit-content" }) }}
      >
        {children}
      </div>
    </TileContext.Provider>
  );
};

interface TileHeadProps extends Omit<MainProps, "children"> {
  title: string | JSX.Element | React.ReactNode;
  children?: React.ReactNode;
}

const Head = ({ style, children, title }: TileHeadProps) => {
  const context = useContext(TileContext);

  return (
    <div className={styles.tile_head} style={style}>
      <div
        className={`${styles.tile_head_title} ${context?.foldable && styles.clickable}`}
        onClick={() => context?.foldable && context?.setIsFolded(() => !context?.isFolded)}
      >
        {context?.foldable && (
          <div className={styles.tile_head_chevron}>
            <>{context?.isFolded ? <LucideIcon name="chevron-right" /> : <LucideIcon name="chevron-down" />}</>
          </div>
        )}
        <div className={styles.tile_head_label}>
          <h4 style={{ margin: 0, overflow: "hidden" }}>{title}</h4>
        </div>
      </div>
      {children}
    </div>
  );
};
Tile.Head = Head;

const Controls = ({ style, children }: MainProps) => {
  return (
    <div className={styles.tile_head_controls} style={style}>
      {children}
    </div>
  );
};
Head.Controls = Controls;

const Fixed = ({ children }: MainProps) => {
  return <>{children}</>;
};
Controls.Fixed = Fixed;

const Unfolded = ({ children }: MainProps) => {
  const context = useContext(TileContext);
  return <>{!context?.isFolded && children}</>;
};
Controls.Unfolded = Unfolded;

const Folded = ({ children }: MainProps) => {
  const context = useContext(TileContext);
  return <>{context?.isFolded && children}</>;
};
Controls.Folded = Folded;

const Body = ({ style, children }: MainProps) => {
  const [height, setHeight] = useState<number>();
  const ref = React.useRef<HTMLDivElement>(null);
  const context = useContext(TileContext);
  return (
    <>
      {!context?.isFolded && (
        <div
          className={`${styles.tile_body} ${context?.resizible && !context.isFolded ? styles.tile_resizer : ""} ${
            !context?.foldable
              ? styles.tile_body_unfolded
              : context?.isFolded
              ? styles.tile_body_folded
              : styles.tile_body_unfolded
          }`}
          style={{
            ...style,
            ...(context?.resizible && !context?.isFolded && height && { height: height }),
          }}
          ref={ref}
        >
          {children}
        </div>
      )}
      {context?.resizible && !context?.isFolded && (
        <FullyCustomResizeHandle
          targetRef={ref}
          orientation="vertical"
          style={{ resize: "vertical" }}
          onChange={(dw, dh, w, h) => {
            if (h) {
              setHeight(h);
            } else if (h === 0) {
              setHeight(undefined);
              context?.setIsFolded(() => true);
            }
          }}
        />
      )}
    </>
  );
};
Tile.Body = Body;
